import { useRef, useState, useCallback } from "react";

// Records mic audio while exposing a live Web Audio AnalyserNode for waveform
// visualization. stop() resolves the recording as base64 (raw audio never stored).
export function useAudioRecorder() {
  const [recording, setRecording] = useState(false);
  const [analyser, setAnalyser] = useState(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const resolveRef = useRef(null);
  const mimeRef = useRef("audio/webm");
  // Technical metadata of the last recording (never content): duration, bytes, MIME and
  // input peak level — lets the backend refuse silent captures (muted / OS-blocked mic)
  // instead of sending silence to Whisper, which hallucinates "you".
  const metaRef = useRef(null);
  const levelRef = useRef({ peak: 0, reliable: false });
  const timerRef = useRef(null);
  const startedRef = useRef(0);

  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    setAnalyser(null);
  }, []);

  const start = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const an = ctx.createAnalyser();
      an.fftSize = 128;
      source.connect(an);
      setAnalyser(an);
      ctx.resume?.().catch(() => {});
      levelRef.current = { peak: 0, reliable: false };
      const buf = new Uint8Array(an.fftSize);
      timerRef.current = setInterval(() => {
        if (ctx.state !== "running") return;
        an.getByteTimeDomainData(buf);
        let m = 0;
        for (let i = 0; i < buf.length; i++) m = Math.max(m, Math.abs(buf[i] - 128));
        levelRef.current = { peak: Math.max(levelRef.current.peak, m / 128), reliable: true };
      }, 50);
    } catch (e) { /* waveform/level meter is optional */ }

    // Pick a container the browser can actually record. iOS Safari only supports
    // audio/mp4 (NOT webm) — hardcoding webm produced undecodable audio and Whisper
    // silence-hallucinations ("…Amara.org"). Use a supported type and report it.
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus", "audio/ogg"];
    let chosen = "";
    try {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported) {
        chosen = candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
      }
    } catch { /* fall through */ }
    const rec = chosen ? new MediaRecorder(stream, { mimeType: chosen }) : new MediaRecorder(stream);
    chunksRef.current = [];
    rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
    rec.onstop = () => {
      const type = rec.mimeType || chosen || "audio/webm";
      mimeRef.current = type;
      const blob = new Blob(chunksRef.current, { type });
      metaRef.current = {
        duration_ms: Math.round(performance.now() - startedRef.current), bytes: blob.size, mime: type,
        peak: Math.round(levelRef.current.peak * 1000) / 1000, level_reliable: levelRef.current.reliable,
      };
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = String(reader.result).split(",")[1] || "";
        resolveRef.current?.(b64);
        resolveRef.current = null;
      };
      reader.readAsDataURL(blob);
      cleanup();
    };
    recorderRef.current = rec;
    rec.start();
    startedRef.current = performance.now();
    setRecording(true);
  }, [cleanup]);

  const stop = useCallback(() => new Promise((resolve) => {
    resolveRef.current = resolve;
    try { recorderRef.current?.stop(); } catch { resolve(""); }
    setRecording(false);
  }), []);

  return { recording, analyser, start, stop, audioMime: mimeRef, audioMeta: metaRef };
}
